/**
 * Agent Signup Flow - 3 Simple Steps
 * Inspired by Claude/ChatGPT onboarding
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowRight, 
  faArrowLeft,
  faCheck,
  faUser,
  faBuilding,
  faCreditCard
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StepOne from './components/StepOne';
import StepTwo from './components/StepTwo';
import StepThree from './components/StepThree';
import agentPortalService from '../../services/agentPortal.service';
import axiosInstance from '../../helpers/api';
import './AgentSignup.css';

export interface SignupData {
  // Step 1
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  
  // Step 2
  desiredSubdomain: string;
  brokerageName: string;
  licenseNumber?: string;
  mlsId?: string;
  agreedToTerms?: boolean;
  
  // Step 3
  stripePaymentMethodId?: string;
}

const AgentSignup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // If returning from Stripe with success, jump to Step 3
  const hasSuccess = searchParams.get('success') === 'true';
  const hasCanceled = searchParams.get('canceled') === 'true';
  const [currentStep, setCurrentStep] = useState(hasSuccess || hasCanceled ? 3 : 1);
  const [signupData, setSignupData] = useState<SignupData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    desiredSubdomain: '',
    brokerageName: '',
    licenseNumber: ''
  });

  const steps = [
    { number: 1, title: 'Your Info', icon: faUser },
    { number: 2, title: 'Your Portal', icon: faBuilding },
    { number: 3, title: 'Payment', icon: faCreditCard }
  ];

  const [sponsorInfo, setSponsorInfo] = useState<any>(null);

  // Pre-fill from sponsor invitation token (lender flow)
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      axiosInstance.get(`/sponsor/invite-prefill?token=${token}`).then(({ data }) => {
        if (data.success) {
          setSponsorInfo(data);
          setSignupData(prev => ({
            ...prev,
            firstName: data.firstName || prev.firstName,
            lastName: data.lastName || prev.lastName,
            email: data.email || prev.email,
            phone: data.phone || prev.phone,
          }));
        }
      }).catch(() => {});
    }
  }, []);

  // Pre-fill from broker invitation token
  useEffect(() => {
    const inviteToken = searchParams.get('invite_token');
    const email = searchParams.get('email');
    const sponsor = searchParams.get('sponsor');
    if (inviteToken) {
      // Store the invite token so we can activate after signup
      localStorage.setItem('brokerInviteToken', inviteToken);
      axiosInstance.post('/broker/invitations/validate-invitation', { token: inviteToken }).then(({ data }) => {
        if (data.success && data.invitation) {
          const inv = data.invitation;
          setSponsorInfo({
            sponsorCompany: inv.brokerageName,
            fundingModel: inv.fundingModel,
            agentFee: inv.agentMonthlyFee,
          });
          setSignupData(prev => ({
            ...prev,
            firstName: inv.agentFirstName || prev.firstName,
            lastName: inv.agentLastName || prev.lastName,
            email: inv.agentEmail || prev.email,
          }));
        }
      }).catch(() => {});
    } else if (sponsor && !searchParams.get('token')) {
      // Came from accept-invitation page with sponsor name but no lender token
      setSponsorInfo({ sponsorCompany: sponsor, fundingModel: 'full_sponsor', agentFee: 0 });
      if (email) setSignupData(prev => ({ ...prev, email }));
    }
  }, []);

  const updateSignupData = (data: Partial<SignupData>) => {
    setSignupData(prev => ({ ...prev, ...data }));
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    // After successful Stripe payment, activate broker invitation if present
    const inviteToken = localStorage.getItem('brokerInviteToken');
    if (inviteToken) {
      try {
        await axiosInstance.post('/broker/invitations/accept-invitation', {
          token: inviteToken,
          email: signupData.email,
        });
        console.log('✅ Broker invitation activated after signup');
      } catch (err) {
        console.warn('⚠️ Broker invitation activation failed:', err);
      } finally {
        localStorage.removeItem('brokerInviteToken');
      }
    }
    navigate('/agent-portal/dashboard');
  };

  return (
    <div className="agent-signup-container">
      {/* Simple Header */}
      <nav className="signup-nav">
        <Container>
          <div className="nav-content">
            <h3 className="portal-logo">AI Search Portal</h3>
            <Button 
              variant="link" 
              className="back-to-home"
              onClick={() => navigate('/agent-portal')}
            >
              ← Back to Home
            </Button>
          </div>
        </Container>
      </nav>

      <Container className="signup-content">
        <Row className="justify-content-center">
          <Col lg={8} xl={6}>
            {/* Sponsor Invitation Banner */}
            {sponsorInfo && (
              <div style={{ background: 'linear-gradient(135deg, #0F1E35, #1E3557)', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(201,149,42,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FontAwesomeIcon icon={faBuilding} style={{ color: '#C9952A', fontSize: 16 }} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Invited by {sponsorInfo.sponsorCompany}</div>
                  <div style={{ fontSize: 12, color: '#8FA3BF' }}>
                    {sponsorInfo.fundingModel === 'full_sponsor' ? '14-day free trial — fully sponsored' :
                     sponsorInfo.fundingModel === 'agent_pays' ? '14-day free trial — $250/mo after trial' :
                     `14-day free trial — $${sponsorInfo.agentFee}/mo after trial (partially sponsored)`}
                  </div>
                </div>
              </div>
            )}

            {/* Progress Steps */}
            <div className="step-indicators">
              {steps.map((step) => (
                <div 
                  key={step.number}
                  className={`step-item ${currentStep >= step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
                >
                  <div className="step-circle">
                    {currentStep > step.number ? (
                      <FontAwesomeIcon icon={faCheck} />
                    ) : (
                      <FontAwesomeIcon icon={step.icon} />
                    )}
                  </div>
                  <span className="step-title">{step.title}</span>
                </div>
              ))}
              <div className="step-line">
                <div 
                  className="step-line-progress" 
                  style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
                />
              </div>
            </div>

            {/* Step Content */}
            <div className="step-content">
              {currentStep === 1 && (
                <StepOne 
                  data={signupData}
                  updateData={updateSignupData}
                  onNext={handleNext}
                />
              )}
              
              {currentStep === 2 && (
                <StepTwo 
                  data={signupData}
                  updateData={updateSignupData}
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                />
              )}
              
              {currentStep === 3 && (
                <StepThree 
                  data={signupData}
                  updateData={updateSignupData}
                  onComplete={handleComplete}
                  onPrevious={handlePrevious}
                />
              )}
            </div>

            {/* Trust Footer */}
            <div className="signup-footer">
              <p className="security-note">
                <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                Your information is secure and encrypted
              </p>
              <p className="help-text">
                Need help? Call us at <strong>(520) 251-9595</strong>
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AgentSignup;